// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Relatorios/RelatoriosFiscais.tsx
//  Relatórios Fiscais: Saídas analítico, Entradas analítico, Vendas por CFOP.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { ArrowUpRight, ArrowDownLeft, Hash, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  useRelFiscalSaidas, useRelFiscalEntradas, useRelVendasCfop,
} from '@/features/relatorios/hooks/useRelatorios';
import { BotaoExportar } from '@/components/BotaoExportar';

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const fmtNum = (v: number, d = 4) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: d }).format(v);
const today   = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const PAGE_SIZES = [10, 20, 50] as const;

function FiltrosPeriodo({ dtInicio, dtFim, setDtInicio, setDtFim }: {
  dtInicio: string; dtFim: string;
  setDtInicio: (v: string) => void; setDtFim: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">De</span>
        <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} className="w-40" />
      </div>
      <div className="flex flex-col gap-1"><span className="text-xs text-muted-foreground">Até</span>
        <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} className="w-40" />
      </div>
    </div>
  );
}

function PaginacaoBar({ pagina, setPagina, tam, setTam, total, totalPaginas }: {
  pagina: number; setPagina: (p: number) => void;
  tam: number; setTam: (t: number) => void;
  total: number; totalPaginas: number;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t text-sm text-muted-foreground">
      <span>{total === 0 ? 'Nenhum registro' : `Total: ${total} itens`}</span>
      <div className="flex items-center gap-3">
        <Select value={String(tam)} onValueChange={(v) => { setTam(Number(v)); setPagina(1); }}>
          <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
          <SelectContent>{PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina - 1)} disabled={pagina <= 1}><ChevronLeft className="h-4 w-4" /></Button>
        <span>Pág {pagina} / {totalPaginas}</span>
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(pagina + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

// ── Tabela de notas analítico ─────────────────────────────────────────────────

function TabelaNotasAnalitico({ data, isLoading, pagina, setPagina, tam, setTam }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any; isLoading: boolean;
  pagina: number; setPagina: (p: number) => void;
  tam: number; setTam: (t: number) => void;
}) {
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Cliente/Fornecedor</TableHead>
              <TableHead>Produto</TableHead>
              <TableHead>CFOP</TableHead>
              <TableHead className="text-right">Qtd</TableHead>
              <TableHead className="text-right">Val.Unit</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">ICMS</TableHead>
              <TableHead className="text-right">PIS</TableHead>
              <TableHead className="text-right">COFINS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.itens.length ? (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.itens.map((r: ReturnType<typeof data.itens[0]>, i: number) => (
              <TableRow key={i}>
                <TableCell className="text-sm text-muted-foreground">{new Date(r.dtMovimento).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell className="font-medium max-w-[160px] truncate" title={r.nomeCliente}>{r.nomeCliente}</TableCell>
                <TableCell className="max-w-[140px] truncate" title={r.descricaoProduto}>{r.descricaoProduto}</TableCell>
                <TableCell className="font-mono text-xs">{r.cfop ?? '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtNum(r.quantidade)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCur(r.valorUnitario)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.valorTotal)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.valorIcms > 0 ? fmtCur(r.valorIcms) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{r.valorPis > 0 ? fmtCur(r.valorPis) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{r.valorCofins > 0 ? fmtCur(r.valorCofins) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {data && <PaginacaoBar pagina={pagina} setPagina={setPagina} tam={tam} setTam={setTam} total={data.total} totalPaginas={data.totalPaginas} />}
    </>
  );
}

// ── Saídas ────────────────────────────────────────────────────────────────────

function TabSaidas() {
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim,    setDtFim]    = useState(today());
  const [pagina,   setPagina]   = useState(1);
  const [tam,      setTam]      = useState(20);

  const { data, isLoading } = useRelFiscalSaidas({ dtInicio, dtFim, pagina, tamanhoPagina: tam });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} setDtInicio={(v) => { setDtInicio(v); setPagina(1); }} setDtFim={(v) => { setDtFim(v); setPagina(1); }} />
        <BotaoExportar endpoint="/api/relatorios/fiscais/saidas/exportar" params={exportParams} />
      </div>
      <TabelaNotasAnalitico data={data} isLoading={isLoading} pagina={pagina} setPagina={setPagina} tam={tam} setTam={setTam} />
    </div>
  );
}

// ── Entradas ──────────────────────────────────────────────────────────────────

function TabEntradas() {
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim,    setDtFim]    = useState(today());
  const [pagina,   setPagina]   = useState(1);
  const [tam,      setTam]      = useState(20);

  const { data, isLoading } = useRelFiscalEntradas({ dtInicio, dtFim, pagina, tamanhoPagina: tam });
  const exportParams = useMemo(() => ({ dtInicio, dtFim }), [dtInicio, dtFim]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} setDtInicio={(v) => { setDtInicio(v); setPagina(1); }} setDtFim={(v) => { setDtFim(v); setPagina(1); }} />
        <BotaoExportar endpoint="/api/relatorios/fiscais/entradas/exportar" params={exportParams} />
      </div>
      <TabelaNotasAnalitico data={data} isLoading={isLoading} pagina={pagina} setPagina={setPagina} tam={tam} setTam={setTam} />
    </div>
  );
}

// ── CFOP ──────────────────────────────────────────────────────────────────────

function TabCfop() {
  const [dtInicio, setDtInicio] = useState(`${new Date().getFullYear()}-01-01`);
  const [dtFim,    setDtFim]    = useState(today());
  const [saida,    setSaida]    = useState(true);

  const { data, isLoading } = useRelVendasCfop({ dtInicio, dtFim, saida });
  const exportParams = useMemo(() => ({ dtInicio, dtFim, saida }), [dtInicio, dtFim, saida]);

  const totalProdutos = data?.reduce((s, x) => s + x.totalProdutos, 0) ?? 0;
  const totalIcms     = data?.reduce((s, x) => s + x.totalIcms,     0) ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <FiltrosPeriodo dtInicio={dtInicio} dtFim={dtFim} setDtInicio={setDtInicio} setDtFim={setDtFim} />
        <Select value={saida ? 'saida' : 'entrada'} onValueChange={(v) => setSaida(v === 'saida')}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="saida">Saídas</SelectItem>
            <SelectItem value="entrada">Entradas</SelectItem>
          </SelectContent>
        </Select>
        <BotaoExportar endpoint="/api/relatorios/fiscais/cfop/exportar" params={exportParams} />
      </div>
      {data && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Total Produtos</p><p className="text-lg font-bold tabular-nums text-blue-600">{fmtCur(totalProdutos)}</p></div>
          <div className="rounded-lg border p-3"><p className="text-xs text-muted-foreground">Total ICMS</p><p className="text-lg font-bold tabular-nums text-orange-600">{fmtCur(totalIcms)}</p></div>
        </div>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>CFOP</TableHead>
              <TableHead className="text-right">Qtd Itens</TableHead>
              <TableHead className="text-right">Total Produtos</TableHead>
              <TableHead className="text-right">ICMS</TableHead>
              <TableHead className="text-right">ICMS-ST</TableHead>
              <TableHead className="text-right">IPI</TableHead>
              <TableHead className="text-right">PIS</TableHead>
              <TableHead className="text-right">COFINS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>)
            ) : !data?.length ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nenhum resultado</TableCell></TableRow>
            ) : data.map((r) => (
              <TableRow key={r.cfop}>
                <TableCell className="font-mono font-semibold">{r.cfop}</TableCell>
                <TableCell className="text-right">{r.qtdItens}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtCur(r.totalProdutos)}</TableCell>
                <TableCell className="text-right tabular-nums">{r.totalIcms > 0 ? fmtCur(r.totalIcms) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{r.totalIcmsSt > 0 ? fmtCur(r.totalIcmsSt) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{r.totalIpi > 0 ? fmtCur(r.totalIpi) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{r.totalPis > 0 ? fmtCur(r.totalPis) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{r.totalCofins > 0 ? fmtCur(r.totalCofins) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Página ────────────────────────────────────────────────────────────────────

const RelatoriosFiscais = () => (
  <div className="p-6 space-y-6">
    <div>
      <h1 className="text-3xl font-bold text-foreground">Relatórios Fiscais</h1>
      <p className="text-muted-foreground mt-1">Análise de notas de saída, entrada e agrupamento por CFOP.</p>
    </div>
    <Tabs defaultValue="saidas">
      <TabsList>
        <TabsTrigger value="saidas"   className="gap-2"><ArrowUpRight   className="h-4 w-4" />Saídas</TabsTrigger>
        <TabsTrigger value="entradas" className="gap-2"><ArrowDownLeft  className="h-4 w-4" />Entradas</TabsTrigger>
        <TabsTrigger value="cfop"     className="gap-2"><Hash           className="h-4 w-4" />Por CFOP</TabsTrigger>
      </TabsList>
      <TabsContent value="saidas">   <Card><CardHeader><CardTitle>Notas de Saída — Analítico</CardTitle></CardHeader><CardContent><TabSaidas /></CardContent></Card></TabsContent>
      <TabsContent value="entradas"> <Card><CardHeader><CardTitle>Notas de Entrada — Analítico</CardTitle></CardHeader><CardContent><TabEntradas /></CardContent></Card></TabsContent>
      <TabsContent value="cfop">     <Card><CardHeader><CardTitle>Vendas por CFOP</CardTitle></CardHeader><CardContent><TabCfop /></CardContent></Card></TabsContent>
    </Tabs>
  </div>
);

export default RelatoriosFiscais;
